import {
    Box,
    Alert,
    AlertDescription,
    Spinner,
    Flex,
    Text,
    AlertIcon,
    RadioGroup,
    Radio,
    Stack,
    Heading,
    Button,
    useDisclosure
} from '@chakra-ui/react'
import { useState, useRef } from 'react'
import { DataTable } from '@/components/datatable/Datatable'
import { useStaleCheckouts } from '@/modules/teacher/hooks/useStaleCheckouts'
import { useClearStaleCheckouts } from '@/modules/teacher/hooks/useClearStaleCheckouts'
import { staleCheckoutColumns } from '@/modules/teacher/utils/tableColumnHeader'
import { SERVICE_SESSION } from '@/modules/signup-page/types'
import PermissionGate from '@/shared/components/PermissionGate'
import { UserTypes } from '@/shared/types/auth'
import { useBranchContext } from '@/modules/teacher/context/BranchContext'
import RestrictedBranchDropdown from '@/components/RestrictedBranchDropdown'
import ClearStaleCheckoutsDialog from './ClearStaleCheckoutsDialog'

function StaleCheckoutTab() {
    const { currentBranch } = useBranchContext()
    const [session, setSession] = useState<SERVICE_SESSION | ''>('')
    const { loading, data, error, refetch } = useStaleCheckouts({
        branchId: currentBranch.value,
        session
    })

    const { isOpen, onOpen, onClose } = useDisclosure()
    const cancelRef = useRef<HTMLButtonElement>(null)
    const { clearStaleCheckouts, clearing } = useClearStaleCheckouts()

    const handleClearStaleCheckouts = async () => {
        if (!currentBranch.value || !session) return
        await clearStaleCheckouts(currentBranch.value, session)
        await refetch()
        onClose()
    }

    const handleSessionChange = (value: string) => setSession(value as SERVICE_SESSION)

    return (
        <Box
            minH={['container.sm', 'container.md']}
            borderRadius={4}
            bg={'white'}
            mx={'auto'}
            py={5}
            px={{ base: 2, md: 17 }}
        >
            <Heading fontSize="normal" mb={2}>
                Choose branch
            </Heading>
            <RestrictedBranchDropdown />

            <Box mb={6}>
                <Heading fontSize="normal" mb={2}>
                    Service Session{' '}
                    <Text as="span" color="red.500">
                        *
                    </Text>
                </Heading>
                <RadioGroup onChange={handleSessionChange} value={session}>
                    <Stack direction="row" spacing={4}>
                        <Radio size="lg" value={SERVICE_SESSION.MORNING}>
                            Morning
                        </Radio>
                        <Radio size="lg" value={SERVICE_SESSION.EVENING}>
                            Evening
                        </Radio>
                    </Stack>
                </RadioGroup>
            </Box>

            {!currentBranch.value ? null : !session ? (
                <Alert variant="subtle" status="warning" borderRadius={'md'} width={'md'}>
                    <AlertIcon />
                    <AlertDescription>
                        Please select a service session above to view stale checkouts.
                    </AlertDescription>
                </Alert>
            ) : loading ? (
                <Flex justify="center" align="center" minH="200px">
                    <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="brand.200"
                        color="brand.500"
                        size="xl"
                    />
                </Flex>
            ) : error ? (
                <Alert status="error" borderRadius={'md'}>
                    <AlertIcon />
                    <AlertDescription>
                        Error loading stale checkouts: {error.message}
                    </AlertDescription>
                </Alert>
            ) : (
                <Box>
                    <Alert status="info" borderRadius={'md'} mb={4} width={'md'}>
                        <AlertIcon />
                        <AlertDescription>
                            Showing children who were checked in during the previous{' '}
                            <b>{session.toLowerCase()}</b> session but have not been
                            checked out yet.
                        </AlertDescription>
                    </Alert>

                    {data.length > 0 ? (
                        <>
                            <Text mb={2} fontWeight="medium" color="gray.600">
                                Total stale checkouts: {data.length}
                            </Text>

                            <PermissionGate
                                allowedTypes={[
                                    UserTypes.IS_ADMIN_TEACHER,
                                    UserTypes.IS_GLOBAL_ADMIN
                                ]}
                            >
                                <Button
                                    size="lg"
                                    w={'sm'}
                                    my={2}
                                    variant="outline"
                                    colorScheme="red"
                                    onClick={onOpen}
                                    isDisabled={data.length === 0}
                                >
                                    Clear Stale Checkouts
                                </Button>
                            </PermissionGate>

                            <DataTable columns={staleCheckoutColumns} data={data} />
                        </>
                    ) : (
                        <Alert status="success" borderRadius={'md'} width={'md'}>
                            <AlertIcon />
                            <AlertDescription>
                                No stale checkouts found. All children have been checked
                                out!
                            </AlertDescription>
                        </Alert>
                    )}
                </Box>
            )}
            <ClearStaleCheckoutsDialog
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={handleClearStaleCheckouts}
                isLoading={clearing}
                cancelRef={cancelRef}
                session={session || ''}
            />
        </Box>
    )
}

export default StaleCheckoutTab
